import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, tap, withLatestFrom, map, catchError, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';

import { IAuroraState } from '@apps/aurora.state';
import * as fromAuthStore from '@apps/auth/store';
import { NotificationService, FilesService, NotfGravity, NotifType } from '@common/services';
import { joinAssets } from '@common/operators/join-assets';
import { Profile } from '@apps/auth/models';
import { TypeAssetName } from '@apps/cmdb/cmdb.constants';
import { getAllTags } from '@apps/cmdb/store';
import { AssetMiscDomainService } from '@apps/cmdb/domain/asset-misc-domain.service';

import * as fromBckpPoliciesActions from './policies.actions';
import {
    getBckpPoliciesQueryString, getBckpPoliciesExportQueryString, getAllPoliciesByIdentifier
} from './policies-selectors';
import { PolicyListDomainService } from '../../domain/policy-list-domain.service';
import { Policy, PolicyDetails } from '../../domain/models';
import { PolicyMiscDomainService } from '../../domain/policy-misc-domain.service';
import { EnrichedAsset, Tag } from '@apps/cmdb/models';
import { Update } from '@ngrx/entity';
import { Assetify } from '@common/domain/assetify';

@Injectable()
export class BckpPoliciesEffects
{

    constructor (
        private actions: Actions,
        private store: Store<IAuroraState>,
        private policyListDomainService: PolicyListDomainService,
        private policyMiscDomainService: PolicyMiscDomainService,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private assetMiscDomainService: AssetMiscDomainService
    )
    { }

    bckpPoliciesODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpPoliciesQueryString),
                        this.store.select(getAllTags),
                        (_, qs, allTags) => ({
                            qs,
                            allTags
                        })
                    ),
                    switchMap(({ qs, allTags }) =>
                        this.policyListDomainService.getLatestPolicies(qs)
                            .pipe(
                                joinAssets(this.assetMiscDomainService, TypeAssetName.POLICY, allTags),
                                map((policies: Policy[]) =>
                                    fromBckpPoliciesActions.BckpPoliciesODataLoaded({ policies })
                                ),
                                catchError(() =>
                                    of(fromBckpPoliciesActions.BckpPoliciesODataFailed())
                                )
                            )
                    )
                )
    );

    bckpPoliciesDetailsRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesDetailsRequested),
                    switchMap( params =>
                        this.policyListDomainService.getPolicyDetails(params.documentId)
                            .pipe(
                                map((policyDetails: PolicyDetails) =>
                                    fromBckpPoliciesActions.BckpPoliciesDetailsLoaded({ policyDetails })
                                ),
                                catchError(() =>
                                    of(fromBckpPoliciesActions.BckpPoliciesDetailsFailed())
                                )
                            )
                    )
                )
    );

    bckpPoliciesFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesFilterApplied),
                    tap((params: any) =>
                    {
                        this.store.dispatch(fromBckpPoliciesActions.BckpPoliciesCleared());
                        this.store.dispatch(fromBckpPoliciesActions.BckpPoliciesODataInitialized({
                            filter: params.filter
                        }));
                    }),
                    tap(() =>
                        this.store.dispatch(fromBckpPoliciesActions.BckpPoliciesODataRequested())
                    )
                ),
        { dispatch: false }
    );

    bckpPoliciesExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesExportRequested),
                    withLatestFrom(this.store.select(getBckpPoliciesExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.policyMiscDomainService.export(qs, params.format)
                    ),
                    map((response: HttpEvent<Blob>) =>
                        fromBckpPoliciesActions.BckpPoliciesExportSucceeded({ response })
                    )
                )
    );

    bckpPoliciesExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob> }, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    bckpPoliciesTagAttachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagAttachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.attachTag(TypeAssetName.POLICY, params.identifier, params.name, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpPoliciesActions.BckpPoliciesTagAttachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpPoliciesTagAttachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagAttachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllPoliciesByIdentifier({ identifier: params.enrichedAsset.identifier })),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([policies, tags]: [Policy[], Tag[]]) =>
                                {
                                    policies.forEach((policy: Policy) =>
                                    {
                                        const updatedPolicy: Update<Policy> = {
                                            id: policy.storeId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpPoliciesActions.BckpPoliciesTagAttachSucceeded({ updatedPolicy: updatedPolicy }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpPoliciesTagAttachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagAttachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.ATTACH.TITLE',
                                'CMDB.ASSETS.ATTACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpPoliciesTagDetachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagDetachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.detachTag(TypeAssetName.POLICY, params.identifier, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpPoliciesActions.BckpPoliciesTagDetachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpPoliciesTagDetachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagDetachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllPoliciesByIdentifier({ identifier: params.enrichedAsset.identifier })),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([policies, tags]: [Policy[], Tag[]]) =>
                                {
                                    policies.forEach((policy: Policy) =>
                                    {
                                        const updatedPolicy: Update<Policy> = {
                                            id: policy.storeId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpPoliciesActions.BckpPoliciesTagDetachSucceeded({ updatedPolicy: updatedPolicy }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpPoliciesTagDetachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpPoliciesActions.BckpPoliciesTagDetachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.DETACH.TITLE',
                                'CMDB.ASSETS.DETACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );
}
